// dynamic-single-event.ts
export type Listener<Detail> = (e: CustomEvent<Detail>) => void;

export function createCustomEventBus<Detail = unknown>(eventName: string) {
  const bus = new EventTarget();

  function dispatch(
    detail?: Detail,
    init?: Omit<CustomEventInit<Detail>, 'detail'> & {
      bubbles?: boolean;
      composed?: boolean;
      cancelable?: boolean;
    },
  ) {
    bus.dispatchEvent(
      new CustomEvent<Detail>(eventName, {
        detail,
        bubbles: false,
        composed: false,
        cancelable: false,
        ...init,
      }),
    );
  }

  function on(handler: Listener<Detail>, options?: AddEventListenerOptions | boolean) {
    const wrapped = (e: Event) => handler(e as CustomEvent<Detail>);
    bus.addEventListener(eventName, wrapped, options);
    return () => bus.removeEventListener(eventName, wrapped, options);
  }

  function once(handler: Listener<Detail>, options?: AddEventListenerOptions | boolean) {
    const un = on((e) => {
      un();
      handler(e);
    }, options);
    return un;
  }

  function off(handler: Listener<Detail>, options?: EventListenerOptions | boolean) {
    // 주의: on에서 wrapping을 했기 때문에 동일 레퍼런스가 아니라면 off로는 지우기 어려움
    // 필요하면 아래 멀티 버스처럼 핸들러 맵을 유지하세요.
    bus.removeEventListener(eventName, handler as any, options);
  }

  function waitFor(signal?: AbortSignal): Promise<CustomEvent<Detail>> {
    return new Promise((resolve, reject) => {
      const controller = new AbortController();
      const { signal: local } = controller;
      const wrapped = (e: Event) => {
        controller.abort();
        resolve(e as CustomEvent<Detail>);
      };
      bus.addEventListener(eventName, wrapped, { signal: local, once: true });
      if (signal)
        signal.addEventListener(
          'abort',
          () => {
            controller.abort();
            reject(new DOMException('Aborted', 'AbortError'));
          },
          { once: true },
        );
    });
  }

  return { dispatch, on, once, off, waitFor };
}

// 사용 예시
type SelectedCharacterPayload = any;
export const productDetailSelectedCharacterChanged = createCustomEventBus<SelectedCharacterPayload>(
  'product-detail:selected-character-changed',
);
export type SelectedOptionGroupDetail = unknown;
export const productDetailSelectedOptionGroupChanged = createCustomEventBus<SelectedOptionGroupDetail>(
  'product-detail:selected-option-group-changed',
);
